import type { EventInput } from '@fullcalendar/core';
import type { CalendarEvent, CalendarEventDraft, CalendarEventInput } from './types';

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function toTimeInput(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function parseLocalDate(date: string, time = '00:00'): Date {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}

export function draftFromEvent(event?: CalendarEvent | null): CalendarEventDraft {
  if (!event) {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    return {
      title: '',
      description: '',
      startDate: toDateInput(start),
      endDate: toDateInput(end),
      startTime: toTimeInput(start),
      endTime: toTimeInput(end),
      allDay: false,
      location: '',
      url: '',
    };
  }
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);
  if (event.allDay) {
    const endInclusive = new Date(end);
    endInclusive.setDate(endInclusive.getDate() - 1);
    return {
      id: event.id,
      title: event.title,
      description: event.description ?? '',
      startDate: toDateInput(start),
      endDate: toDateInput(endInclusive),
      startTime: '00:00',
      endTime: '00:00',
      allDay: true,
      location: event.location ?? '',
      url: event.url ?? '',
    };
  }
  return {
    id: event.id,
    title: event.title,
    description: event.description ?? '',
    startDate: toDateInput(start),
    endDate: toDateInput(end),
    startTime: toTimeInput(start),
    endTime: toTimeInput(end),
    allDay: false,
    location: event.location ?? '',
    url: event.url ?? '',
  };
}

export function draftToApiInput(draft: CalendarEventDraft): CalendarEventInput {
  if (draft.allDay) {
    const startAt = parseLocalDate(draft.startDate, '00:00');
    const endDay = parseLocalDate(draft.endDate, '00:00');
    endDay.setDate(endDay.getDate() + 1);
    return {
      title: draft.title.trim(),
      description: draft.description.trim() || undefined,
      startAt: startAt.toISOString(),
      endAt: endDay.toISOString(),
      allDay: true,
      location: draft.location.trim() || undefined,
      url: draft.url.trim() || undefined,
    };
  }
  const startAt = parseLocalDate(draft.startDate, draft.startTime);
  const endAt = parseLocalDate(draft.endDate, draft.endTime);
  return {
    title: draft.title.trim(),
    description: draft.description.trim() || undefined,
    startAt: startAt.toISOString(),
    endAt: endAt.toISOString(),
    allDay: false,
    location: draft.location.trim() || undefined,
    url: draft.url.trim() || undefined,
  };
}

export function apiEventToFullCalendar(event: CalendarEvent): EventInput {
  if (event.allDay) {
    const start = event.startAt.slice(0, 10);
    const end = event.endAt.slice(0, 10);
    return {
      id: event.id,
      title: event.title,
      start,
      end,
      allDay: true,
      extendedProps: { raw: event },
    };
  }
  return {
    id: event.id,
    title: event.title,
    start: event.startAt,
    end: event.endAt,
    allDay: false,
    extendedProps: { raw: event },
  };
}

export function formatEventWhen(event: CalendarEvent): string {
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);
  if (event.allDay) {
    const endInclusive = new Date(end);
    endInclusive.setDate(endInclusive.getDate() - 1);
    if (toDateInput(start) === toDateInput(endInclusive)) {
      return start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }
    return `${start.toLocaleDateString()} – ${endInclusive.toLocaleDateString()}`;
  }
  return `${start.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} – ${end.toLocaleTimeString(undefined, { timeStyle: 'short' })}`;
}

/** Wide fetch window for upcoming events (no practical upper date cap). */
export function upcomingEventsRange(): { from: string; to: string } {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setFullYear(to.getFullYear() + 10);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function pickUpcomingEvents(events: CalendarEvent[], limit = 10): CalendarEvent[] {
  const now = new Date();
  return events
    .filter((e) => new Date(e.endAt) > now)
    .sort((a, b) => a.startAt.localeCompare(b.startAt))
    .slice(0, limit);
}

export function calendarEventHref(event: CalendarEvent, calendarPath: string): string {
  const external = event.url?.trim();
  if (external) return external;
  const base = calendarPath.endsWith('/') ? calendarPath : `${calendarPath}/`;
  return `${base}?event=${encodeURIComponent(event.id)}`;
}

export function isExternalCalendarLink(href: string): boolean {
  return /^https?:\/\//i.test(href);
}
